import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useHistory } from "react-router-dom";
import { getRouteById } from "../../services/Route";

// Import icons
import RouteIcon from "@mui/icons-material/Route";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SalesmanSelectingPopup from "../../components/SalesmanSelectingPopup";
import CustomerSelectingPopup from "../../components/CustomerSelectingPopup";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px 0 rgba(0,0,0,0.06)",
  },
}));

const AvatarLarge = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(16),
  height: theme.spacing(16),
  fontSize: "3rem",
  backgroundColor: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
  "& .MuiTableHead-root": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`route-tabpanel-${index}`}
      aria-labelledby={`route-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `route-tab-${index}`,
    "aria-controls": `route-tabpanel-${index}`,
  };
}

function RouteView() {
  const history = useHistory();
  const { routeId } = useParams();

  // State management
  const [route, setRoute] = useState(null);
  const [routeError, setRouteError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const [openSalesPersonSelection, setOpenSalesPersonSelection] =
    useState(false);
  const [openCustomerSelection, setOpenCustomerSelection] = useState(false);
  const [selectedSalesman, setSelectedSalesman] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [customerSkipList, setCustomerSkipList] = useState([]);
  const [salesmanSkipList, setSalesmanSkipList] = useState([]);

  // Format phone number
  const formatPhoneNumber = (phoneNumberString) => {
    if (!phoneNumberString) return "N/A";
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumberString;
  };

  // Get route initials for avatar
  const getRouteInitials = (routeName) => {
    if (!routeName) return "RT";
    const words = routeName.split(" ");
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    return routeName.substring(0, 2).toUpperCase();
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch route data
  const fetchRouteData = async () => {
    setIsLoading(true);
    try {
      let result = await getRouteById(routeId);
      if (result !== "Something went wrong...") {
        setRoute(result);

        // Normalize customer skip list → array of customerIds
        const customerIds = (result.customers || []).map((c) => c.customerId);
        setCustomerSkipList(customerIds);

        // Normalize salesperson skip list → array of userIds
        const salespersonIds = (result.salespersons || []).map((s) => s.userId);
        setSalesmanSkipList(salespersonIds);
      } else {
        setRouteError("Unable to fetch route data!");
      }
    } catch (error) {
      console.error("Error fetching route data:", error);
      setRouteError("An error occurred while fetching route data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchRouteData();
  }, []);

  // Handle navigate back
  const handleGoBack = () => {
    history.goBack();
  };

  // Handle update customers
  const handleUpdateCustomers = () => {
    history.push(`/template/route_update_customers/${routeId}`);
  };

  // Handle update salespersons
  const handleUpdateSalespersons = () => {
    history.push(`/template/route_update_salespersons/${routeId}`);
  };

  // Salesman selection popup handlers
  const handleOpenSalesmanSelection = () => {
    setOpenSalesPersonSelection(true);
  };

  const handleCloseSalesmanSelection = () => {
    setOpenSalesPersonSelection(false);
    // setFilter({
    //   ...filter,
    //   _salespersonIds: selectedSalesman,
    // });
  };

  const handleOpenCustomerSelection = () => {
    setOpenCustomerSelection(!openCustomerSelection);
    if (openCustomerSelection) {
      //   setFilter({
      //     ...filter,
      //     _customerIds: selectedCustomer,
      //   });
    }
  };

  return (
    <StyledPaper elevation={0}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : routeError ? (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="error">
            {routeError}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      ) : route ? (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <RouteIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Route Details
                </Typography>
                <Typography variant="body2">
                  View and manage route information
                </Typography>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={2}>
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                  >
                    Back
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() =>
                      history.push(`/template/update_route/${routeId}`)
                    }
                  >
                    Edit Route
                  </ActionButton>
                </Stack>
              </Grid>
            </Grid>
          </HeaderCard>

          <Grid container spacing={3}>
            {/* Left column - Route overview */}
            <Grid item xs={12} md={4}>
              <InfoCard>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <AvatarLarge>{getRouteInitials(route.routeName)}</AvatarLarge>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {route.routeName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    Route ID: {route.routeId}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha("#2196f3", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StoreIcon color="primary" />
                        <Typography variant="body2" color="textSecondary">
                          Total Customers
                        </Typography>
                      </Stack>
                      <Typography variant="h6" fontWeight="bold">
                        {route.customers?.length || 0}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha("#4caf50", 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon color="success" />
                        <Typography variant="body2" color="textSecondary">
                          Total Salespersons
                        </Typography>
                      </Stack>
                      <Typography variant="h6" fontWeight="bold">
                        {route.salespersons?.length || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </InfoCard>
            </Grid>

            {/* Right column - Detailed information */}
            <Grid item xs={12} md={8}>
              <InfoCard>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="route tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <StyledTab
                      label="Customers"
                      icon={<StoreIcon />}
                      iconPosition="start"
                      {...a11yProps(0)}
                    />
                    <StyledTab
                      label="Salespersons"
                      icon={<PersonIcon />}
                      iconPosition="start"
                      {...a11yProps(1)}
                    />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                  <Box
                    sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        Assigned Customers
                      </Typography>
                      <ActionButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleOpenCustomerSelection}
                      >
                        Update Customers
                      </ActionButton>
                    </Stack>

                    {route.customers && route.customers.length > 0 ? (
                      <StyledTableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <StyledTableCell>Code</StyledTableCell>
                              <StyledTableCell>Customer Name</StyledTableCell>
                              <StyledTableCell>Phone</StyledTableCell>
                              <StyledTableCell>Email</StyledTableCell>
                              <StyledTableCell>Status</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {route.customers.map((customer) => (
                              <TableRow key={customer.customerId}>
                                <TableCell>{customer.customerCode}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {customer.customerName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {customer.phone ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <PhoneIcon
                                        fontSize="small"
                                        sx={{ fontSize: 16 }}
                                      />
                                      <Typography variant="body2">
                                        {formatPhoneNumber(customer.phone)}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      N/A
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {customer.email ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <EmailIcon
                                        fontSize="small"
                                        sx={{ fontSize: 16 }}
                                      />
                                      <Typography variant="body2">
                                        {customer.email}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      N/A
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={customer.status}
                                    color={
                                      customer.status === "ACTIVE"
                                        ? "success"
                                        : "error"
                                    }
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: 1 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </StyledTableContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <StoreIcon
                          sx={{
                            fontSize: 48,
                            color: "text.secondary",
                            opacity: 0.5,
                            mb: 2,
                          }}
                        />
                        <Typography variant="body1" color="textSecondary">
                          No customers are assigned to this route.
                        </Typography>
                        <ActionButton
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleUpdateCustomers}
                          sx={{ mt: 2 }}
                        >
                          Add Customers
                        </ActionButton>
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <Box
                    sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        Assigned Salespersons
                      </Typography>
                      <ActionButton
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleOpenSalesmanSelection}
                      >
                        Update Salespersons
                      </ActionButton>
                    </Stack>

                    {route.salespersons && route.salespersons.length > 0 ? (
                      <StyledTableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <StyledTableCell>User ID</StyledTableCell>
                              <StyledTableCell>Name</StyledTableCell>
                              <StyledTableCell>Phone</StyledTableCell>
                              <StyledTableCell>Email</StyledTableCell>
                              <StyledTableCell>Status</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {route.salespersons.map((salesperson) => (
                              <TableRow key={salesperson.userId}>
                                <TableCell>{salesperson.userId}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {salesperson.fname} {salesperson.lname}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {salesperson.phone ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <PhoneIcon
                                        fontSize="small"
                                        sx={{ fontSize: 16 }}
                                      />
                                      <Typography variant="body2">
                                        {formatPhoneNumber(salesperson.phone)}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      N/A
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {salesperson.email ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <EmailIcon
                                        fontSize="small"
                                        sx={{ fontSize: 16 }}
                                      />
                                      <Typography variant="body2">
                                        {salesperson.email}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      N/A
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      salesperson.enabled
                                        ? "Active"
                                        : "Inactive"
                                    }
                                    color={
                                      salesperson.enabled ? "success" : "error"
                                    }
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: 1 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </StyledTableContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <PersonIcon
                          sx={{
                            fontSize: 48,
                            color: "text.secondary",
                            opacity: 0.5,
                            mb: 2,
                          }}
                        />
                        <Typography variant="body1" color="textSecondary">
                          No salespersons are assigned to this route.
                        </Typography>
                        <ActionButton
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleUpdateSalespersons}
                          sx={{ mt: 2 }}
                        >
                          Add Salespersons
                        </ActionButton>
                      </Box>
                    )}
                  </Box>
                </TabPanel>
              </InfoCard>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="textSecondary">
            No route data found
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
          >
            Go Back
          </Button>
        </Box>
      )}

      {openSalesPersonSelection && (
        <SalesmanSelectingPopup
          setOpenSalesmanTable={setOpenSalesPersonSelection}
          openSalesmanTable={openSalesPersonSelection}
          handleCloseSalesmanTable={handleCloseSalesmanSelection}
          setSelectedSalesman={setSelectedSalesman}
          selectedSalesman={selectedSalesman}
          isOneChoise={false}
          skipList={salesmanSkipList}
        />
      )}

      {openCustomerSelection && (
        <CustomerSelectingPopup
          setOpenCustomerTable={setOpenCustomerSelection}
          openCustomerTable={openCustomerSelection}
          handleCloseCustomerTable={handleOpenCustomerSelection}
          setSelectedCustomer={setSelectedCustomer}
          selectedCustomer={selectedCustomer}
          isOneChoise={true}
          skipList={customerSkipList}
        />
      )}
    </StyledPaper>
  );
}

export default RouteView;
